import Vue from 'vue';
import Router from 'vue-router';

Vue.use(Router);

/* Layout */
import Layout from '@/layout';
import AUTH from '@/constants/authority-codes';

/**
 * Note: sub-menu only appear when route children.length >= 1
 * Detail see: https://panjiachen.github.io/vue-element-admin-site/guide/essentials/router-and-nav.html
 *
 * hidden: true                   if set true, item will not show in the sidebar(default is false)
 * alwaysShow: true               if set true, will always show the root menu
 *                                if not set alwaysShow, when item has more than one children route,
 *                                it will becomes nested mode, otherwise not show the root menu
 * redirect: noRedirect           if set noRedirect will no redirect in the breadcrumb
 * name:'router-name'             the name is used by <keep-alive> (must set!!!)
 * meta : {
    roles: ['admin','editor']    control the page roles (you can set multiple roles)
    title: 'title'               the name show in sidebar and breadcrumb (recommend set)
    icon: 'svg-name'             the icon show in the sidebar
    noCache: true                if set true, the page will no be cached(default is false)
    affix: true                  if set true, the tag will affix in the tags-view
    breadcrumb: false            if set false, the item will hidden in breadcrumb(default is true)
    activeMenu: '/example/list'  if set path, the sidebar will highlight the path you set
  }
 */

/**
 * constantRoutes
 * a base page that does not have permission requirements
 * all roles can be accessed
 */
export const constantRoutes = [
  {
    path: '/redirect',
    component: Layout,
    hidden: true,
    children: [
      {
        path: '/redirect/:path*',
        component: () => import('@/views/redirect/index')
      }
    ]
  },
  {
    path: '/login',
    component: () => import('@/views/login/index'),
    hidden: true
  },
  {
    path: '/auth-redirect',
    component: () => import('@/views/login/auth-redirect'),
    hidden: true
  },
  {
    path: '/404',
    component: () => import('@/views/error-page/404'),
    hidden: true
  },
  {
    path: '/401',
    component: () => import('@/views/error-page/401'),
    hidden: true
  },
  {
    hidden: true,
    path: '/',
    component: Layout,
    redirect: '/console',
    children: [
      {
        path: 'console',
        component: () => import('@/views/console/index'),
        name: 'Console',
        meta: { title: '控制台', icon: 'dashboard', affix: true }
      }
    ]
  },
  {
    path: '/subject-assist-demo',
    component: Layout,
    hidden: true,
    children: [
      {
        path: 'index',
        component: () => import('@/views/certificate/demo/subject-assist'),
        name: 'SubjectAssistDemo',
        meta: { title: '科目辅助核算演示' }
      }
    ]
  },
  {
    path: '/feedback',
    component: Layout,
    hidden: true,
    children: [
      {
        path: '',
        component: () => import('@/views/feedback/user'),
        name: 'UserFeedback',
        meta: { title: '用户反馈' }
      }
    ]
  },

  {
    path: '/profile',
    component: Layout,
    redirect: '/profile/index',
    hidden: true,
    children: [
      {
        path: 'index',
        component: () => import('@/views/profile/index'),
        name: 'Profile',
        meta: { title: 'Profile', icon: 'user', noCache: true }
      }
    ]
  },
  {
    path: '/wholesale/boss-report',
    component: () => import('@/views/wholesale/boss-report'),
    meta: {
      title: '老板一张表'
    },
    hidden: true
  },
  {
    path: '/wholesale/detail-report',
    component: () => import('@/views/wholesale/detail-report'),
    meta: {
      title: '明细账本'
    },
    hidden: true
  },
  {
    path: '/wholesale/profit-report',
    component: () => import('@/views/wholesale/profit-report'),
    meta: {
      title: '经营情况（利润）表'
    },
    hidden: true
  }
];

/**
 * asyncRoutes
 * the routes that need to be dynamically loaded based on user roles
 */
export const asyncRoutes = [
  {
    path: '/certificate',
    component: Layout,
    redirect: '/certificate/certificateList',
    alwaysShow: true, // will always show the root menu
    name: 'Certificate',
    meta: {
      title: '凭证',
      icon: 'documentation',
      menuCode: AUTH.CERT
    },
    children: [
      {
        path: 'addCertificate',
        click: () => {
          router.push({
            path: '/certificate/addCertificate',
            query: { r: new Date().getTime() }
          });
        },
        component: () => import('@/views/certificate/editor/add'),
        name: 'AddCertificate',
        meta: {
          title: '凭证录入',
          singleTab: true,
          menuCode: AUTH.CERT_CE
        }
      },
      {
        path: 'editCertificate',
        component: () => import('@/views/certificate/editor/edit'),
        name: 'EditCertificate',
        hidden: true,
        meta: {
          title: '凭证编辑'
        }
      },
      {
        path: 'certificateList',
        component: () => import('@/views/certificate/query/certificate'),
        name: 'CertificateList',
        meta: {
          title: '凭证列表',
          menuCode: AUTH.CERT_CL
        }
      },
      {
        path: 'billList',
        component: () => import('@/views/certificate/query/bill'),
        name: 'BillList',
        hidden: true,
        meta: {
          title: '出入库单详情'
        }
      },
      {
        path: 'allCertificate',
        component: () => import('@/views/certificate/query/summary'),
        name: 'AllCertificate',
        meta: {
          title: '凭证汇总表',
          menuCode: AUTH.CERT_CSM
        }
      },
      {
        path: 'businessCertificate',
        component: () => import('@/views/certificate/business/certificate'),
        name: 'BusinessCertificate',
        meta: {
          title: '业务凭证',
          menuCode: AUTH.CERT_BV,
          accountSetTypes: [0, 1, 9]
        }
      },
      {
        path: 'businessVoucherNew',
        component: () => import('@/views/certificate/business/voucher-new'),
        name: 'BusinessVoucherNew',
        meta: {
          title: '业务凭证(New)',
          menuCode: AUTH.CERT_BVN,
          accountSetTypes: [1, 2, 9]
        }
      }
    ]
  },
  {
    path: '/ledger',
    component: Layout,
    redirect: '/ledger/detail',
    alwaysShow: true, // will always show the root menu
    name: 'Ledger',
    meta: {
      title: '账簿',
      icon: 'zhangbu',
      menuCode: AUTH.LDG
    },
    children: [
      {
        path: 'detail',
        click: () => {
          router.push({
            path: '/ledger/detail',
            query: { r: new Date().getTime() }
          });
        },
        component: () => import('@/views/ledger/detail'),
        name: 'LedgerDetail',
        meta: {
          title: '明细账',
          menuCode: AUTH.LDG_DL
        }
      },
      {
        path: 'summary',
        click: () => {
          router.push({
            path: '/ledger/summary',
            query: { r: new Date().getTime() }
          });
        },
        component: () => import('@/views/ledger/summary'),
        name: 'LedgerSummary',
        meta: {
          title: '总账',
          menuCode: AUTH.LDG_GL
        }
      },
      {
        path: 'subjectBalance',
        click: () => {
          router.push({
            path: '/ledger/subjectBalance',
            query: { r: new Date().getTime() }
          });
        },
        component: () => import('@/views/ledger/subjectBalance'),
        name: 'LedgerSubjectBalance',
        meta: {
          title: '科目余额表',
          menuCode: AUTH.LDG_SB
        }
      },
      {
        path: 'accProjectDetail',
        click: () => {
          router.push({
            path: '/ledger/accProjectDetail',
            query: { r: new Date().getTime() }
          });
        },
        component: () => import('@/views/ledger/accProjectDetail'),
        name: 'LedgerAccProjectDetail',
        meta: {
          title: '核算项目明细账',
          menuCode: AUTH.LDG_AP_DL
        }
      },
      {
        path: 'accProjectBalance',
        click: () => {
          router.push({
            path: '/ledger/accProjectBalance',
            query: { r: new Date().getTime() }
          });
        },
        component: () => import('@/views/ledger/accProjectBalance'),
        name: 'LedgerAccProjectBalance',
        meta: {
          title: '核算项目余额表',
          menuCode: AUTH.LDG_AP_BAL
        }
      }
    ]
  },
  {
    path: '/report',
    component: Layout,
    redirect: '/report/assetload',
    alwaysShow: true, // will always show the root menu
    name: 'Report',
    meta: {
      title: '报表',
      icon: 'chart',
      menuCode: AUTH.RPT
    },
    children: [
      {
        path: 'assetload',
        click: () => {
          router.push({
            path: '/report/assetload',
            query: { r: new Date().getTime() }
          });
        },
        component: () => import('@/views/report/assetload'),
        name: 'ReportAssetload',
        meta: {
          title: '资产负债表',
          menuCode: AUTH.RPT_BS
        }
      },
      {
        path: 'profit',
        click: () => {
          router.push({
            path: '/report/profit',
            query: { r: new Date().getTime() }
          });
        },
        component: () => import('@/views/report/profit'),
        name: 'ReportProfit',
        meta: {
          title: '利润表',
          menuCode: AUTH.RPT_PS
        }
      },
      {
        path: 'profitDepartment',
        click: () => {
          router.push({
            path: '/report/profitDepartment',
            query: { r: new Date().getTime() }
          });
        },
        component: () => import('@/views/report/profit/department'),
        name: 'ReportProfitDepartment',
        meta: {
          title: '门店利润表',
          menuCode: AUTH.RPT_SPS
        }
      },
      {
        path: 'cashflow',
        click: () => {
          router.push({
            path: '/report/cashflow',
            query: { r: new Date().getTime() }
          });
        },
        component: () => import('@/views/report/cashflow'),
        name: 'ReportCashflow',
        meta: {
          title: '现金流量表',
          menuCode: AUTH.RPT_CFS
        }
      },
      {
        path: 'adjust',
        component: () => import('@/views/report/adjust'),
        name: 'ReportAdjust',
        meta: {
          title: '调整现金流量表'
        },
        hidden: true
      },
      {
        path: 'custom',
        component: () => import('@/views/report/custom/index'),
        name: 'CustomReport',
        meta: {
          title: '自定义报表',
          menuCode: AUTH.RPT_CR,
          version: 'Beta'
        }
      },
      {
        path: 'customEdit',
        component: () => import('@/views/report/custom/edit'),
        name: 'CustomReportEdit',
        meta: {
          title: '编辑自定义报表',
          singleTab: true
        },
        hidden: true
      }
    ]
  },
  {
    path: '/reportMerge',
    component: Layout,
    redirect: '/reportMerge/assetloadMerge',
    alwaysShow: true, // will always show the root menu
    name: 'ReportMerge',
    meta: {
      title: '合并报表',
      icon: 'chart',
      menuCode: AUTH.MRG_RPT,
      version: 'Beta'
    },
    children: [
      {
        path: 'assetloadMerge',
        click: () => {
          router.push({
            path: '/reportMerge/assetloadMerge',
            query: { r: new Date().getTime() }
          });
        },
        component: () => import('@/views/reportMerge/assetloadMerge'),
        name: 'ReportAssetloadMerge',
        meta: {
          title: '综合资产负债表',
          menuCode: AUTH.MRG_RPT_CBS
        }
      },
      {
        path: 'profitMerge',
        click: () => {
          router.push({
            path: '/reportMerge/profitMerge',
            query: { r: new Date().getTime() }
          });
        },
        component: () => import('@/views/reportMerge/profitMerge'),
        name: 'ReportProfitMerge',
        meta: {
          title: '综合利润表',
          menuCode: AUTH.MRG_RPT_CPS
        }
      },
      {
        path: 'cashflowMerge',
        click: () => {
          router.push({
            path: '/reportMerge/cashflowMerge',
            query: { r: new Date().getTime() }
          });
        },
        component: () => import('@/views/reportMerge/cashflowMerge'),
        name: 'ReportCashflowMerge',
        meta: {
          title: '综合现金流量表',
          menuCode: AUTH.MRG_RPT_CCFS
        }
      }
    ]
  },
  {
    path: '/asset',
    component: Layout,
    redirect: '/report/manage',
    alwaysShow: true, // will always show the root menu
    name: 'Asset',
    meta: {
      title: '固定资产',
      icon: 'component',
      menuCode: AUTH.FXD_AST
    },
    children: [
      {
        path: 'manage',
        component: () => import('@/views/asset/manage'),
        name: 'AssetManage',
        meta: {
          title: '固定资产管理',
          menuCode: AUTH.FXD_AST_FAM
        }
      },
      {
        path: 'depreciation',
        component: () => import('@/views/asset/depreciation'),
        name: 'AssetDepreciation',
        meta: {
          title: '固定资产折旧统计表'
        }
      },
      {
        path: 'sub-ledger',
        component: () => import('@/views/asset/sub-ledger'),
        name: 'AssetSubLedger',
        meta: {
          title: '固定资产明细账',
          menuCode: AUTH.FXD_AST_FADL
        }
      },
      {
        path: 'type',
        component: () => import('@/views/asset/type'),
        name: 'AssetType',
        meta: {
          title: '固定资产类别',
          menuCode: AUTH.FXD_AST_FAC
        }
      }
    ]
  },
  {
    path: '/amortization',
    component: Layout,
    redirect: '/amortization/manage',
    meta: {
      title: '长期待摊',
      icon: 'list',
      menuCode: AUTH.LTDE
    },
    children: [
      {
        path: 'manage',
        component: () => import('@/views/amortization/manage'),
        name: 'AmortizationManage',
        meta: {
          title: '摊销费用管理',
          menuCode: AUTH.LTDE_AEM
        }
      },
      {
        path: 'quick',
        component: () => import('@/views/amortization/quick'),
        name: 'QuickGenerator',
        meta: {
          title: ' 快速生成',
          menuCode: AUTH.LTDE_QG
        }
      }
    ]
  },
  {
    path: '/cashier',
    component: Layout,
    redirect: '/cashier/diary',
    alwaysShow: true,
    name: 'Cashier',
    meta: {
      title: '出纳',
      icon: 'money',
      menuCode: AUTH.CSH
    },
    children: [
      {
        path: 'diary',
        name: 'CashierDiary',
        meta: {
          title: '日记账',
          menuCode: AUTH.CSH_JNL
        },
        component: () => import('@/views/cashier/diary/index')
      },
      {
        path: 'verifiedLedger',
        name: 'CashierVerifiedLedger',
        meta: {
          title: '核对总账',
          menuCode: AUTH.CSH_REC_GL
        },
        component: () => import('@/views/cashier/verifiedLedger')
      },
      {
        path: 'account',
        name: 'CashierAccount',
        meta: {
          title: '账户',
          menuCode: AUTH.CSH_ACC
        },
        component: () => import('@/views/cashier/account')
      },
      {
        path: 'quick',
        name: 'QuickGen',
        meta: {
          title: '快速生成',
          menuCode: AUTH.CSH_QG
        },
        component: () => import('@/views/cashier/quickData')
      },
      {
        path: 'dividend',
        component: () => import('@/views/cashier/dividend'),
        name: 'Dividend',
        meta: {
          title: '资金结算表',
          menuCode: AUTH.CSH_FSS,
          version: 'Alpha',
          isDividend: true
        }
      }
    ]
  },
  {
    path: '/settle',
    component: Layout,
    redirect: '/settle/settle',
    children: [
      {
        path: 'settle',
        component: () => import('@/views/settle/settle'),
        name: 'Settle',
        meta: {
          title: '结账',
          icon: 'education',
          menuCode: AUTH.STL
        }
      }
    ]
  },
  {
    path: '/operations',
    component: Layout,
    redirect: '/operations/report',
    hidden: true,
    children: [
      {
        path: 'report',
        component: () => import('@/views/operations/report'),
        name: 'OperationsReport',
        meta: {
          title: '运营报表'
        }
      },
      {
        path: 'feedback',
        component: () => import('@/views/feedback/manage'),
        name: 'OperationsFeedback',
        meta: {
          title: '反馈管理'
        }
      }
    ]
  },
  {
    path: '/setting',
    component: Layout,
    redirect: '/setting/config',
    alwaysShow: true, // will always show the root menu
    name: 'Setting',
    meta: {
      title: '设置',
      icon: 'gear',
      menuCode: AUTH.SET
    },
    children: [
      {
        path: 'subject',
        component: () => import('@/views/setting/subject/index.vue'),
        name: 'SettingSubject',
        meta: {
          title: '科目',
          menuCode: AUTH.SET_SUB
        }
      },
      {
        path: 'assist',
        component: () => import('@/views/setting/assist/index.vue'),
        name: 'SettingAssist',
        meta: {
          title: '辅助核算',
          menuCode: AUTH.SET_AA,
          noCache: true
        }
      },
      {
        path: 'business',
        component: () => import('@/views/setting/business/index.vue'),
        name: 'SettingBusiness',
        meta: {
          title: '业务科目对照表',
          menuCode: AUTH.SET_BSM
        }
      },
      {
        path: 'relasubject',
        component: () => import('@/views/setting/relasubject/index.vue'),
        name: 'RelasubjectNew',
        meta: {
          title: '科目关联设置',
          menuCode: AUTH.SET_SLS
        }
      },
      {
        path: 'templates',
        component: () => import('@/views/setting/templates/index.vue'),
        name: 'Templates',
        meta: {
          title: '业务凭证模板',
          menuCode: AUTH.SET_CT
        }
      },
      {
        path: 'balance',
        component: () => import('@/views/setting/balance/index.vue'),
        name: 'SettingBalance',
        meta: {
          title: '财务初始余额',
          menuCode: AUTH.SET_FIB
        }
      },
      {
        path: 'relasystem',
        component: () => import('@/views/setting/relasystem/index.vue'),
        name: 'SettingRelasystem',
        meta: {
          title: '关联进销存',
          menuCode: AUTH.SET_LNK_INVENTORY,
          noCache: true
        }
      },
      {
        path: 'config',
        component: () => import('@/views/setting/config/index.vue'),
        name: 'SettingConfig',
        meta: {
          title: '系统参数',
          menuCode: AUTH.SET_SYSP
        }
      }
    ]
  },
  // 404 page must be placed at the end !!!
  { path: '*', redirect: '/404', hidden: true }
];

const createRouter = () =>
  new Router({
    // mode: 'history', // require service support
    scrollBehavior: () => ({ y: 0 }),
    routes: constantRoutes
  });

const router = createRouter();

// Detail see: https://github.com/vuejs/vue-router/issues/1234#issuecomment-357941465
export function resetRouter () {
  const newRouter = createRouter();
  router.matcher = newRouter.matcher; // reset router
}

export default router;
